import { decodeBase64Url, encodeBase64Url } from "../lib/base64";

const csrfToken = document
  .querySelector("meta[name='csrf-token']")
  ?.getAttribute("content") || "";

async function fetchJson(url, payload) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": csrfToken,
    },
    body: JSON.stringify(payload),
    credentials: "same-origin",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = data.error || "Request failed";
    throw new Error(error);
  }

  return data;
}

function prepareAuthenticationOptions(payload) {
  const options = { ...payload.publicKey };
  options.challenge = decodeBase64Url(options.challenge);

  if (Array.isArray(options.allowCredentials) && options.allowCredentials.length > 0) {
    options.allowCredentials = options.allowCredentials.map((cred) => ({
      ...cred,
      id: decodeBase64Url(cred.id),
    }));
  } else {
    delete options.allowCredentials;
  }

  return options;
}

function extractTransports(response) {
  if (typeof response.getTransports === "function") {
    try {
      return response.getTransports();
    } catch (_err) {
      return [];
    }
  }

  return [];
}

function readEmail(el) {
  const fieldId = el.dataset.emailInput;
  let input;

  if (fieldId) {
    input = document.getElementById(fieldId);
  }

  if (!input) {
    const form = el.closest("form") || document;
    input = form.querySelector("input[type='email']");
  }

  if (!input) return undefined;

  const value = input.value.trim();
  return value.length > 0 ? value : undefined;
}

export default {
  mounted() {
    this.handleClick = this.handleClick.bind(this);
    this.el.addEventListener("click", this.handleClick);
    this.loading = false;
  },

  destroyed() {
    this.el.removeEventListener("click", this.handleClick);
  },

  async handleClick(event) {
    event.preventDefault();

    if (this.loading) return;

    if (!window.PublicKeyCredential) {
      this.pushEvent("passkey-error", {
        message: "Passkeys are not supported on this device.",
      });
      return;
    }

    this.loading = true;
    this.el.setAttribute("disabled", "disabled");

    try {
      const { optionsUrl, verifyUrl } = this.el.dataset;
      const email = readEmail(this.el);
      const optionsPayload = await fetchJson(optionsUrl, email ? { email } : {});

      const publicKey = prepareAuthenticationOptions(optionsPayload);
      const credential = await navigator.credentials.get({ publicKey });

      if (!credential) {
        throw new Error("Passkey authentication was cancelled.");
      }

      const { response } = credential;

      const verifyPayload = {
        response: {
          rawId: encodeBase64Url(credential.rawId),
          authenticatorData: encodeBase64Url(response.authenticatorData),
          clientDataJSON: encodeBase64Url(response.clientDataJSON),
          signature: encodeBase64Url(response.signature),
          userHandle: response.userHandle ? encodeBase64Url(response.userHandle) : null,
          type: credential.type,
          transports: extractTransports(response),
        },
      };

      const result = await fetchJson(verifyUrl, verifyPayload);

      if (result.redirect_to) {
        window.location.assign(result.redirect_to);
      } else {
        this.pushEvent("passkey-error", { message: "No redirect provided by server." });
      }
    } catch (error) {
      this.pushEvent("passkey-error", { message: error.message || String(error) });
    } finally {
      this.loading = false;
      this.el.removeAttribute("disabled");
    }
  },
};
